import { message } from "antd";
import { Form, Formik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import useGetData from "../../api/useGetData";
import usePostData from "../../api/usePostData";
import AppPhoneInput from "../../common/AppPhoneInput";
import { registerPhoneValidation } from "../../utils/validations";
import Button from "../Button/Button";
import Recaptcha from "../../components/Recaptcha/Recaptcha";
import { RegisterTracking } from "../../utils/mixpanel";
import PropTypes from "prop-types";
import { getNewTime } from "../../utils/getOTPExpirationTime";

/**
 * A component that represents the phone step form of the registeration process.
 * It is stored in a separate component because there are two different places where the registeration takes place with some differences in each.
 * It is intended for this component to not call the useContext hook directly, but for its parent to do so instead.
 *
 * @param {Object} props
 * @param {{phone: string}} props.initialValues - The initial values of the form, the phone
 * @param {(phone: string)=>void} props.phoneStepSubmit - A callback function that gets called when the user submits the form successfully
 * @param {boolean} props.showRecpatcha - A flag to show the otp. Only used in the account info step of booking process.
 * @param {string} props.recaptchaToken - The recpatcha Token, a controlled state
 * @param {(token: string)=>void} props.setRecaptchaToken - The recpatcha token set state
 * @param {{current: any}} props.recaptchaRef - A reference to the google recpatcha.
 * @returns {JSX.Element}
 */

const PhoneStepForm = ({
  initialValues,
  phoneStepSubmit,
  recaptchaToken,
  setRecaptchaToken,
  showRecpatcha,
  recaptchaRef,
}) => {
  const [showResend, setShowResend] = useState(false); // State to show resend button
  const [phoneOtp, setPhoneOtp] = useState(false);
  const [error, setError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [timer, setTimer] = useState(60); // Timer state
  const { t } = useTranslation("global");
  const { postData, errors: postErrors } = usePostData();

  const [hidePhoneCreateAccountButton, setHidePhoneCreateAccountButton] =
    useState(false);
  const [showPhoneOTPScreen, setShowPhoneOTPScreen] = useState(false);
  const handleVerify = useCallback(async () => {
    try {
      if(phoneOtp.length !== 6){
        setError("Please enter OTP");
      }
      else{
        const response = await confirmPhone({
          params: { phone: phoneNumber, otp: phoneOtp },
        });
        if (response?.status === 200) {
          phoneStepSubmit(phoneNumber);
        }
        if (response?.error) {
          setError(response?.error);
          message.error(`${response?.error }`);
        }
      }
    } catch (error) {
      setError(error?.response?.data?.msg);
      message.error(`${error?.response?.data?.msg }`);
    }
  }, [phoneOtp]);

  useEffect(() => {
    if (phoneOtp.length === 6) {
      handleVerify();
    }
  }, [phoneOtp, handleVerify]);

  const onPhoneSubmit = async (values, { setSubmitting }) => {
    if (recaptchaToken === null && showRecpatcha) {
      return message.error(t("recaptchaRequired"));
    }
    const checkPasswordResponse = await postData({
      route: "api/method/airport_transport.api.user.check_phone",
      data: {
        phone: values.phone,
      },
      params: {
        headers: {
          recaptchaToken,
        },
      },
    });
    if (checkPasswordResponse.status === 200) {
      const response = await getMobileOTP({ params: { phone: values.phone } });
      if (response?.status === 200) {
        setPhoneNumber(values.phone);
        setShowPhoneOTPScreen(true);
        setHidePhoneCreateAccountButton(true);
        setTimer(30);
      }
    }
    else{RegisterTracking(false, "Phone already exists");}
    setSubmitting(false);
  };

  const { getData: getMobileOTP, errors } = useGetData({
    route: "api/method/airport_transport.api.user.send_mobile_otp",
    notLoadData: true,
  });

  const { getData: confirmPhone } = useGetData({
    route: "api/method/airport_transport.api.user.confirm_phone",
    notLoadData: true,
  });

  const handleChange = (value) => {
    setPhoneOtp(value);
    setError(""); // Clear error message when user types
  };

  useEffect(() => {
    if (errors) {
      if (errors === "SMS OTP not allowed for country") {
        phoneStepSubmit(phoneNumber);
      } else {
        message.error(`${errors}`);
      }
    }
  }, [errors]);

  useEffect(() => {
    if (postErrors) {
      const errorMsg = postErrors?.error;
      if (errorMsg === "Mobile number already exists") {
        message.error(t("errors.mobile_number_must_be_unique"));
      } else {
        message.error(`${errorMsg}`);
      }
      if (showRecpatcha) {
        setRecaptchaToken(null);
        recaptchaRef?.current?.reset();
      }
    }
  }, [postErrors, t]);

  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      setShowResend(true); // Show resend button when timer completes
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleResend = async () => {
       // Handle resend OTP logic
       try {
        const response = await getMobileOTP({ params: { phone:phoneNumber } });
         
         // Redirect to OTP verification screen
         if (response?.status === 200) {
           setTimer(getNewTime()); // Reset timer
           setShowResend(false); // Hide resend button
           const msg = response?.data?.msg?.toLowerCase();
           if (msg === "otp sent") {
             setPhoneOtp("");
             setError(""); // Clear error message when user types
             message.success(t("otp_sent"));
           } else {
             message.success(`${msg}`);
           }
         }
       } catch (error) {
         message.error(`${error?.response?.data?.msg}`);
       }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={registerPhoneValidation}
      onSubmit={onPhoneSubmit}
    >
      {({ setFieldValue, errors, touched }) => (
        <Form>
          <AppPhoneInput
            label={t("hero.enter_phone_text")}
            name="phone"
            onChange={(phone) => {
              setFieldValue("phone", phone);
              setPhoneNumber(phone);
            }}
            value={phoneNumber}
            error={errors.phone}
            touched={touched.phone}
          />
          {showRecpatcha && (
            <Recaptcha
              recaptchaRef={recaptchaRef}
              onChange={(value) => {
                setRecaptchaToken(value);
              }}
            />
          )}
          {!hidePhoneCreateAccountButton && (
            <div className="text-center mt-6">
              <Button
                className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium rounded text-sm px-5 py-2.5 me-2 mb-2"
                label={t("next_text")}
                type="submit"
              />
            </div>
          )}

          {showPhoneOTPScreen && (
            <>
              <div className="mt-3 flex flex-col justify-center">
                <label
                  htmlFor={phoneOtp}
                  className="block mb-2 mt-2 lg:mt-1  text-sm font-medium text-gray-900 dark:text-white"
                >
                  {t("hero.enter_otp_text")}
                </label>
                <div dir="ltr">
                  <OtpInput
                    inputStyle="otp-input"
                    value={phoneOtp}
                    onChange={handleChange}
                    numInputs={6}
                    renderInput={(props, i) => <input {...props} key={i} />}
                  />
                </div>

                {error && (
                  <div className="text-base text-text_warning font-semibold my-2">
                    {error}
                  </div>
                )}
              </div>

              <div className="mt-2 flex flex-row justify-start items-center">
                <div className="mr-1">
                  {showResend ? (
                    <Button
                      className="bg-background_steel_blue w-20 text-center text-text_white hover:bg-gray-100 font-medium rounded text-sm px-1 py-2 me-2 mb-2"
                      label={t("resend_text")}
                      type="button"
                      onClick={handleResend}
                    />
                  ) : (
                    `${t("hero.resend_otp_text")} - ${Math.floor(timer / 60)
                      .toString()
                      .padStart(2, "0")}:${(timer % 60)
                      .toString()
                      .padStart(2, "0")}`
                  )}
                </div>
              </div>

              <div className="my-3">
                <Button
                  className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium rounded text-sm px-5 py-2.5 me-2 mb-2"
                  label={t("verify_text")}
                  type="button"
                  onClick={handleVerify}
                />
              </div>
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};

PhoneStepForm.propTypes = {
  initialValues: PropTypes.shape({
    phone: PropTypes.string.isRequired,
  }).isRequired,
  phoneStepSubmit: PropTypes.func.isRequired,
  recaptchaToken: PropTypes.string,
  setRecaptchaToken: PropTypes.func.isRequired,
  showRecpatcha: PropTypes.bool,
  recaptchaRef: PropTypes.shape({
    current: PropTypes.any,
  }),
};

export default PhoneStepForm;

